import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useBranding } from "@/lib/queries";
import { applyTheme } from "@/lib/theme";
import { lastBranding } from "@/lib/lastOrg";
import { pickBackground } from "@/lib/backgrounds";
import { cn } from "@/lib/utils";
import { Backdrop } from "./Backdrop";
import { BrandLogo } from "./BrandLogo";

const TAGLINES = [
  "Minden négyzetméter a helyén. ✨",
  "Kevesebb táblázat, több kávé. ☕",
  "A bérleti díjak nem számolják ki magukat. Na jó, itt igen.",
  "Rendezett irodaház, nyugodt délután.",
  "Ma is minden számla a helyére kerül. 📬",
];

/**
 * Belépés, jelszó és meghívó oldalak kerete. Az arculatot a host (whitelabel domain),
 * az ?org= paraméter, vagy az utoljára használt szervezet adja.
 */
export function AuthLayout({ children, orgHint }: { children: React.ReactNode; orgHint?: string | null }) {
  const hint = orgHint ?? new URLSearchParams(window.location.search).get("org") ?? lastBranding()?.slug ?? null;
  const { data: b } = useBranding(hint);
  const [tagline] = useState(() => TAGLINES[Math.floor(Math.random() * TAGLINES.length)]);
  const hasImage = !!pickBackground(b?.backgroundUrls, 0);

  useEffect(() => {
    if (b) applyTheme(b.primaryColor, b.secondaryColor);
  }, [b]);

  return (
    <div className="relative flex min-h-dvh">
      {/* Asztali: bal oldali képes panel */}
      <div className="relative hidden flex-1 lg:block">
        <Backdrop urls={b?.backgroundUrls} />
        <div className="relative z-10 flex h-full flex-col justify-between p-10">
          <div className="glass w-fit rounded-2xl px-5 py-4 shadow-soft">
            <BrandLogo logoUrl={b?.logoUrl} brandName={b?.brandName} full className="h-16" />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className={cn(
              "max-w-md text-3xl font-bold leading-snug",
              "text-white",
              hasImage && "drop-shadow-[0_2px_12px_rgba(0,0,0,.35)]",
            )}
          >
            {tagline}
          </motion.p>
        </div>
      </div>

      {/* Űrlap oldal (mobilon a háttér mögötte van) */}
      <div className="relative flex w-full items-center justify-center px-4 py-10 lg:w-[520px] lg:shrink-0 lg:bg-background lg:px-12">
        <Backdrop urls={b?.backgroundUrls} className="lg:hidden" />
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: "spring", stiffness: 220, damping: 24 }}
          className="relative z-10 w-full max-w-sm rounded-3xl bg-card/95 p-7 shadow-lift backdrop-blur lg:bg-transparent lg:p-0 lg:shadow-none"
        >
          <div className="mb-8 lg:hidden">
            <BrandLogo logoUrl={b?.logoUrl} brandName={b?.brandName} full />
          </div>
          {children}
        </motion.div>
      </div>
    </div>
  );
}
